import React, { FC, memo, useCallback } from 'react';
import dayjs, { Dayjs } from 'dayjs';
import { useAppTranslation } from 'app/config/i18Config/hooks';
import { TableFilter, useTableFilterContext } from 'features/TableFilter';
import { PaymentsStatisticsFilterKeys } from '../../model/types';
import { CLIENT_DATE_FORMAT } from 'shared/utils/constants';
import { WarehouseSelect } from './WarehouseSelect';
import { RangeDatePicker } from 'shared/ui/RangeDatePicker/RangeDatePicker';

export const PaymentsStatisticsFilter: FC = memo(() => {
  const { t } = useAppTranslation(['contracts', 'common']);

  const { filters, changeFilters, clearCurrentFilters } = useTableFilterContext<PaymentsStatisticsFilterKeys>();

  const selectWarehouse = useCallback(
    (value: string[]) => {
      changeFilters({
        key: PaymentsStatisticsFilterKeys.selectedWarehouses,
        label: `${t('Selected')}: ${value.length}`,
        value,
        title: t('Warehouse'),
      });
    },
    [changeFilters, t],
  );

  const changeStartDate = useCallback(
    (value: Nullable<Dayjs>) => {
      const clientFormatDateFrom = dayjs(value).startOf('day').format(CLIENT_DATE_FORMAT);

      changeFilters({
        key: PaymentsStatisticsFilterKeys.startDate,
        label: t('From {{dueDateFrom}}', { dueDateFrom: clientFormatDateFrom }),
        value,
        title: t('Date range'),
      });
    },
    [changeFilters, t],
  );

  const changeEndDate = useCallback(
    (value: Nullable<Dayjs>) => {
      const clientFormatDateTo = dayjs(value).startOf('day').format(CLIENT_DATE_FORMAT);

      changeFilters({
        key: PaymentsStatisticsFilterKeys.endDate,
        label: t('To {{dueDateTo}}', { dueDateTo: clientFormatDateTo }),
        value,
        title: t('Date range'),
      });
    },
    [changeFilters, t],
  );

  return (
    <TableFilter>
      <div className="space-y-2">
        <WarehouseSelect values={filters.selectedWarehouses?.value} onChange={selectWarehouse} onClear={clearCurrentFilters} />

        <RangeDatePicker
          placeholder={[t('From'), t('To')]}
          label={t('Date range')}
          onChangeFrom={changeStartDate}
          onChangeTo={changeEndDate}
          values={[filters.startDate?.value && dayjs(filters.startDate?.value), filters.endDate?.value && dayjs(filters.endDate?.value)]}
        />
      </div>
    </TableFilter>
  );
});
